// SAST business-time helpers — spec §4 item 7 (Amendment 2026-07-10). The plants run
// on Africa/Johannesburg time; South Africa observes NO daylight saving, so SAST is a
// FIXED UTC+2 offset year-round. That means every helper below is plain, deterministic
// offset arithmetic — no IANA tz-database lookup / `Intl.DateTimeFormat` timeZone
// option needed — and is correct on ANY host regardless of the machine's own TZ
// (notably: the deployed API Lambda, which has no TZ env var beyond the
// defense-in-depth one set in infra/lib/api-stack.ts).
//
// THE BUG THIS FIXES: every date-range report and consolidated-invoicing period used
// to build UTC day boundaries from offset-less local datetime strings
// (`${date}T00:00:00.000`), which `z.coerce.date()` parses as UTC on whatever host
// runs the parse. On a developer's SAST machine that accidentally "worked"; on the
// UTC Lambda, a transaction posted 00:00-02:00 SAST was silently attributed to the
// PREVIOUS SAST day. `sastDayStart`/`sastDayEnd` produce the correct UTC instant
// unconditionally, so a client that sends the resulting ISO string is correct
// wherever the server happens to run.
//
// PINNED CONTRACT — other goals import these names; do not rename without a spec
// amendment: `BUSINESS_TZ`, `BUSINESS_UTC_OFFSET`, `sastDayStart`, `sastDayEnd`,
// `formatSastDate`, `formatSastDateTime`.
/** IANA identifier for the business timezone — documentation/labelling only (no
 * Intl/tz-database lookup actually uses this string; see header). */
export const BUSINESS_TZ = 'Africa/Johannesburg';
/** SAST's fixed UTC offset, year-round (no DST). */
export const BUSINESS_UTC_OFFSET = '+02:00';
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
function assertDateOnly(dateStr) {
    if (!DATE_ONLY_RE.test(dateStr)) {
        throw new Error(`Expected a 'YYYY-MM-DD' business date, got: ${JSON.stringify(dateStr)}`);
    }
}
/**
 * The UTC instant of 00:00:00.000 SAST for a 'YYYY-MM-DD' business date — the
 * inclusive START of that SAST business day.
 *
 * Worked example: `sastDayStart('2026-07-10')` = 2026-07-09T22:00:00.000Z. SAST
 * midnight is 2 hours AHEAD of UTC, so it falls at 22:00 UTC on the PREVIOUS
 * calendar day.
 */
export function sastDayStart(dateStr) {
    assertDateOnly(dateStr);
    return new Date(`${dateStr}T00:00:00.000${BUSINESS_UTC_OFFSET}`);
}
/**
 * The UTC instant of 23:59:59.999 SAST for a 'YYYY-MM-DD' business date — the
 * inclusive END of that SAST business day.
 *
 * Worked example: `sastDayEnd('2026-07-10')` = 2026-07-10T21:59:59.999Z.
 */
export function sastDayEnd(dateStr) {
    assertDateOnly(dateStr);
    return new Date(`${dateStr}T23:59:59.999${BUSINESS_UTC_OFFSET}`);
}
/** Shifts a UTC instant by the fixed SAST offset and reads its UTC calendar
 * fields back — equivalent to (and cheaper/more portable in a Lambda with no
 * tz-database guarantees than) an `Intl.DateTimeFormat` `timeZone` lookup, exactly
 * because the offset never varies (see header). Internal — not exported. */
function toSastParts(d) {
    const shifted = new Date(d.getTime() + 2 * 60 * 60 * 1000);
    return {
        y: shifted.getUTCFullYear(),
        m: shifted.getUTCMonth() + 1,
        day: shifted.getUTCDate(),
        hh: shifted.getUTCHours(),
        mm: shifted.getUTCMinutes(),
    };
}
/** Renders a UTC instant as its SAST calendar date, `'YYYY-MM-DD'` — for document
 * and report headers (invoice issue/due dates, dispatch dates, printed period
 * ranges) so a Lambda running in UTC still prints the date the plant experienced. */
export function formatSastDate(d) {
    const { y, m, day } = toSastParts(d);
    return `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
/** Renders a UTC instant as SAST wall-clock date + time, `'YYYY-MM-DD HH:mm'`. */
export function formatSastDateTime(d) {
    const { y, m, day, hh, mm } = toSastParts(d);
    return `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')} ${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}
//# sourceMappingURL=time.js.map